import { Carousel } from '@liveauctioneers/hammer-ui-core/carousel';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ComponentProps, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import ItemCard from '@/components/GridItemCard/Card/Card';
import TimedOrItemShelfCard, {
    CarouselItemShelfCard,
} from '@/components/GridItemCard/Card/MultiCardOptions/TimedOrItemShelfCard/TimedOrItemShelfCard';
import SideScroller from '@/components/SideScroller/SideScroller';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { getRecommendationTypeFromTitle } from '@/utils/amplitude/analytics/item/recommendationTypes';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import noop from '@/utils/noop';

import FluidItemGrid from '../GridItemCard/FluidItemGrid/FluidItemGrid';
import { RouteButton } from '../RouteButton/RouteButton';

const GRID_CHANGE_WIDTH = 550;
const MAX_ITEMS_FOR_MOBILE = 6;
const VISIBILITY_THRESHOLD = 0.5;

type Props = {
    allowBidding?: boolean;
    className?: string;
    hideLiveButton?: boolean;
    identifier: string;
    itemIds: number[];
    links?: ReactNode;
    onVisibleItemIdsChange?: (visibleItemIds: number[]) => void;
    seeAllUrl?: string;
    showBidStatus?: boolean;
    showSoldBadge?: boolean;
    shouldShowViewAll?: boolean;
    testid?: string;
    title: string;
    trackItemClick?: Function;
} & Partial<ComponentProps<typeof Carousel>>;

export const ItemShelfCarousel = ({
    className = '',
    identifier,
    itemIds = [],
    links = false,
    onVisibleItemIdsChange,
    seeAllUrl = '',
    shouldShowViewAll = false,
    testid,
    title,
    trackItemClick = noop,
    ...carouselProps
}: Props) => {
    const wide = useIsWiderThan(GRID_CHANGE_WIDTH);
    const formatMessage = useAppFormatMessage();
    const recommendationType = getRecommendationTypeFromTitle(title);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!onVisibleItemIdsChange) {
            return;
        }

        if (!wide) {
            onVisibleItemIdsChange(itemIds.slice(0, Math.min(itemIds.length, MAX_ITEMS_FOR_MOBILE)));
            return;
        }

        const container = containerRef.current;
        const scrollContainer = container?.querySelector('[role="list"]') as HTMLElement | null;
        if (!scrollContainer) {
            return;
        }

        const listItems = scrollContainer.querySelectorAll(':scope > [role="listitem"]');
        if (listItems.length === 0) {
            return;
        }

        const visibleIndices = new Set<number>();

        const updateVisibleIds = () => {
            const ids = Array.from(visibleIndices)
                .sort((a, b) => a - b)
                .map((i) => itemIds[i])
                .filter((id) => typeof id === 'number');
            onVisibleItemIdsChange(ids);
        };

        const listItemsArray = Array.from(listItems);

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const index = listItemsArray.indexOf(entry.target);
                    if (index === -1) {
                        continue;
                    }
                    if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
                        visibleIndices.add(index);
                    } else {
                        visibleIndices.delete(index);
                    }
                }
                updateVisibleIds();
            },
            { root: scrollContainer, threshold: VISIBILITY_THRESHOLD }
        );

        listItemsArray.forEach((el) => observer.observe(el));

        return () => {
            observer.disconnect();
        };
    }, [wide, itemIds, onVisibleItemIdsChange]);

    const trackClick = useCallback(
        (itemId) => {
            trackItemClick(itemId);
        },
        [trackItemClick]
    );

    const carouselCards = useMemo(
        () =>
            itemIds?.map((itemId, index) => ({
                card: (
                    <ItemCard
                        itemCardOption={CarouselItemShelfCard}
                        itemId={itemId}
                        key={`${index}-${identifier}`}
                        recommendationPosition={index + 1}
                        recommendationType={recommendationType}
                        trackClick={() => trackClick(itemId)}
                    />
                ),
                key: `${index}-${identifier}`,
            })),
        [identifier, itemIds, recommendationType, trackClick]
    );

    if (!itemIds.length) {
        return (
            <div
                className={cn('flex h-[361px] w-full items-center justify-center bg-background-subdued', className)}
                id={identifier}
            >
                <Loading />
            </div>
        );
    }

    const fullCarouselHeader: Pick<ComponentProps<typeof Carousel>, 'title' | 'action'> = {
        ...(seeAllUrl && {
            action: {
                path: seeAllUrl,
                text: String(formatMessage(['viewAll'])),
            },
        }),
        title,
    };

    return (
        <div
            className={cn('flex flex-col bg-background-primary transition-all duration-200 ease-in', className)}
            data-testid={testid}
            id={identifier}
            ref={containerRef}
        >
            {Boolean(links) && links}
            {wide ? (
                <SideScroller
                    cards={carouselCards}
                    className="w-full pb-sm"
                    useOverlayControls
                    {...carouselProps}
                    {...(!links && fullCarouselHeader)}
                />
            ) : (
                <>
                    {!links && <H2 className="ml-[20px] xsMax:ml-0">{title}</H2>}
                    <FluidItemGrid
                        className="mb-[35px] px-[20px]"
                        itemCardOption={TimedOrItemShelfCard}
                        itemIds={itemIds.slice(0, MAX_ITEMS_FOR_MOBILE)}
                        recommendationType={recommendationType}
                    />
                    {shouldShowViewAll && (
                        <div className="mx-auto text-center">
                            <RouteButton
                                className="w-[250px] max-w-[calc(100%-40px)]"
                                to={seeAllUrl}
                            >
                                {formatMessage(['viewAll'])}
                            </RouteButton>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
